import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { 
  DELETE_LOST_ANIMAL,
  UPDATE_LOST_REUNITED_STATUS,
} from '../../services/apollo/mutations/lostAnimalMutations';
import { 
  DELETE_FOUND_ANIMAL,
  UPDATE_FOUND_REUNITED_STATUS,
} from '../../services/apollo/mutations/foundAnimalMutations';
import { DELETE_PSA } from '../../services/apollo/mutations/psaMutations';
import { DELETE_PET_SERVICE } from '../../services/apollo/mutations/petServiceMutations';
import { GET_FEED } from '../../services/apollo/queries/feedQueries';
import { FEED_TYPES } from '../notifications/useCombinedFeed';
import { triggerFeedRefresh } from '../../slices/notificationsSlice';

const usePostActions = () => {
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  // Delete mutations for each post type
  const [deleteLostAnimal] = useMutation(DELETE_LOST_ANIMAL, {
    refetchQueries: [{ query: GET_FEED }],
    onError: (err) => setError(err.message),
  });

  const [deleteFoundAnimal] = useMutation(DELETE_FOUND_ANIMAL, {
    refetchQueries: [{ query: GET_FEED }],
    onError: (err) => setError(err.message),
  });

  const [deletePSA] = useMutation(DELETE_PSA, {
    refetchQueries: [{ query: GET_FEED }],
    onError: (err) => setError(err.message),
  });

  const [deletePetService] = useMutation(DELETE_PET_SERVICE, {
    refetchQueries: [{ query: GET_FEED }],
    onError: (err) => setError(err.message),
  });

  // Update mutations for reunited status
  const [updateLostReunited] = useMutation(UPDATE_LOST_REUNITED_STATUS, {
    onError: (err) => setError(err.message),
  });

  const [updateFoundReunited] = useMutation(UPDATE_FOUND_REUNITED_STATUS, {
    onError: (err) => setError(err.message),
  });

  const deletePost = useCallback(async (postId, postType) => {
    setIsDeleting(true);
    setError(null);

    try {
      switch (postType) {
        case FEED_TYPES.LOST:
        case 'lost':
        case 'LostAnimal':
          await deleteLostAnimal({ variables: { _id: postId } });
          break;
        case FEED_TYPES.FOUND:
        case 'found':
        case 'FoundAnimal':
          await deleteFoundAnimal({ variables: { _id: postId } });
          break;
        case FEED_TYPES.PSA:
        case 'psa':
        case 'PSA':
          await deletePSA({ variables: { _id: postId } });
          break;
        case FEED_TYPES.PET_SERVICE:
        case 'petService':
        case 'PetService':
          await deletePetService({ variables: { _id: postId } });
          break;
        default:
          throw new Error(`Unknown post type: ${postType}`);
      }

      dispatch(triggerFeedRefresh());
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsDeleting(false);
    }
  }, [deleteLostAnimal, deleteFoundAnimal, deletePSA, deletePetService, dispatch]);


  const toggleReunited = useCallback(async (postId, postType, currentReunitedStatus) => {
    setIsUpdating(true);
    setError(null);

    try {
      const newReunitedStatus = !currentReunitedStatus;

      switch (postType) {
        case FEED_TYPES.LOST:
        case 'lost':
        case 'LostAnimal':
          await updateLostReunited({ 
            variables: { _id: postId, reunited: newReunitedStatus } 
          });
          break;
        case FEED_TYPES.FOUND:
        case 'found':
        case 'FoundAnimal':
          await updateFoundReunited({ 
            variables: { _id: postId, reunited: newReunitedStatus } 
          });
          break;
        default:
          throw new Error(`Reunited status only applies to lost/found animals`);
      }

      dispatch(triggerFeedRefresh());
      return { success: true, reunited: newReunitedStatus };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsUpdating(false);
    }
  }, [updateLostReunited, updateFoundReunited, dispatch]);


  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    deletePost,
    toggleReunited,
    isDeleting,
    isUpdating,
    error,
    clearError,
  };
};

export default usePostActions;

